import React from 'react';
import { useState, useEffect } from 'react';
import { EditFieldForm } from '../shared/EditFieldForm';
import { useDispatch, useSelector, shallowEqual, batch } from 'react-redux';
import { Pencil } from '../shared/Pencil';
import Svg from '../shared/Svg';
import PropTypes from 'prop-types';
import { PlusSign } from '../shared/PlusSign';
import { PopOutIcon } from '../shared/PopOutIcon';
import * as MailActions from '../../actions/mail';

import './SelectedRecordItem.scss';

export const SelectedRecordItem = ({
  itemId,
  nameProp,
  imgSrc,
  editCallback,
  savedState,
  showPopOut,
}) => {
  const dispatch = useDispatch();
  const mail = useSelector((state) => state.mail, shallowEqual);
  const isPresave = !!mail.draftId;
  const isSavingToTimeline = useSelector((state) => state.mail.isSavingToTimeline);
  const [selectedState, setSelectedState] = useState(isSavingToTimeline);
  const saveAllEmailRecords = mail.settings?.timelineSettings?.saveAllEmailRecords;
  const [isEditingName, updateEditName] = useState(false);
  const [name, updateName] = useState(nameProp);
  const [nameHover, setNameHover] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const selectedRecords = useSelector((state) => state.mail.selectedRecords);
  const objectsAndDisplayFields = useSelector((state) => state.mail.settings?.objectsAndDisplayFields);

  // reduce popout links into a object where the key is the recordId and the contains the link and name
  const popoutLinks = Object.values(selectedRecords)
      .filter((record) => record.emailMessageId)
      .map((record) => {
        const currentDisplayFields = objectsAndDisplayFields?.find((object) => object.apiName === record.type)?.displayFields.map((field) => field.apiName) || [];
        return {
          link: `${user.salesforceInstanceUrl}/lightning/r/EmailMessage/${record.emailMessageId}/view`,
          name: record?.[currentDisplayFields[0]] || record.Id,
        };
      });

  const clickAction = () => {
    dispatch(MailActions.savingToTimelineToggled(true));
    dispatch(MailActions.savingToTimeline(!selectedState));
    setSelectedState(!selectedState);
  };

  const editNameCallback = (newName, hasChanged) => {
    updateName(newName);
    updateEditName(false);
    if (hasChanged) {
      batch(() => {
        dispatch(MailActions.isSubjectEdited(true));
        dispatch(MailActions.isEmailEdited(true));
      });
    }
    if (editCallback && typeof editCallback === 'function') {
      editCallback(newName, itemId);
    }
  };

  const toggleEditName = () => updateEditName(!isEditingName);

  // if the name prop changes, update the name state
  useEffect(() => {
    updateName(nameProp);
  }, [nameProp]);

  // if the state changes, update the selected state
  useEffect(() => {
    setSelectedState(isSavingToTimeline);
  }, [isSavingToTimeline]);

  return (
    <div className={`selectedRecordItem-container`}>
      <Svg className="selectedRecordItem-image" data={imgSrc}/>
      <div className='selectedRecordItem-details'>
        <EditFieldForm
          fieldProp={name}
          isEditingFieldProp={isEditingName}
          editingFieldCallback={editNameCallback}
          hoverCallback={setNameHover}
        />
      </div>
      <div className='selectedRecordItem-iconsContainer'>
        {(!isEditingName && !savedState) &&
          <Pencil isOuterHovered={nameHover} onClickCallback={toggleEditName} tooltipContent='Edit name'/>
        }
        { showPopOut ?
          <PopOutIcon
            link={popoutLinks.length === 1 && popoutLinks[0].link}
            linkArr={popoutLinks.length > 1 && popoutLinks}
            className={'docrioFileItem-removeMargin'}
          /> :
          <PlusSign
            clickAction={clickAction}
            hovering={false}
            // if set for auto save, we want to grey it out and not present the option to not save.
            savedState={(savedState || saveAllEmailRecords) && (!isPresave || saveAllEmailRecords)}
            selectedState={isSavingToTimeline}
          />
        }
      </div>
    </div>
  );
};

SelectedRecordItem.propTypes = {
  itemId: PropTypes.string,
  nameProp: PropTypes.string,
  imgSrc: PropTypes.string.isRequired,
  editCallback: PropTypes.func,
  saveDispatchAction: PropTypes.func,
  savedState: PropTypes.bool.isRequired,
  showPopOut: PropTypes.bool,
};
